import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { 
  Search, 
  Users, 
  AlertTriangle, 
  CheckCircle, 
  Loader2, 
  DollarSign,
  UserCheck,
  UserX,
  Target 
} from 'lucide-react';
import { useCompanyEmployees } from '@/hooks/leave/useCompanyEmployees';
import { useCompensationAssignments } from '@/hooks/compensation';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import { toast } from '@/hooks/use-toast';
import type { CompensationRule } from '@/services/compensation/types';

interface RuleAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule: CompensationRule | null;
}

export function RuleAssignmentDialog({
  open,
  onOpenChange,
  rule,
}: RuleAssignmentDialogProps) {
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Get currency code at component top level (not inside formatRuleValue function)
  const { currencyCode } = useWorkplaceCurrency();

  const { data: employees, isLoading: employeesLoading } = useCompanyEmployees();
  const { 
    useRuleAssignments, 
    useAssignRule, 
    useUnassignRule,
    useValidateRuleAssignment 
  } = useCompensationAssignments();
  
  const { data: currentAssignments, isLoading: assignmentsLoading } = useRuleAssignments(rule?.id || '');
  const assignRuleMutation = useAssignRule();
  const unassignRuleMutation = useUnassignRule();
  const validateAssignmentMutation = useValidateRuleAssignment();

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSelectedEmployees([]);
      setSearchQuery('');
    }
  }, [open, rule?.id]);

  // Extract currently assigned employee IDs
  const currentlyAssignedIds = currentAssignments?.map(assignment => assignment.profile_id) || [];

  // Filter employees based on search query
  const filteredEmployees = employees?.filter((employee: any) =>
    `${employee.first_name} ${employee.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    employee.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    employee.role.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // Separate assigned and unassigned employees
  const assignedEmployees = filteredEmployees.filter((emp: any) => 
    currentlyAssignedIds.includes(emp.id)
  );
  const unassignedEmployees = filteredEmployees.filter((emp: any) => 
    !currentlyAssignedIds.includes(emp.id)
  );

  const getEmployeeInitials = (employee: any) => {
    return `${employee.first_name?.[0] || ''}${employee.last_name?.[0] || ''}`.toUpperCase();
  };

  const handleEmployeeToggle = (employeeId: string) => {
    setSelectedEmployees(prev =>
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleSelectAll = (employeeList: any[]) => {
    const employeeIds = employeeList.map(emp => emp.id);
    const allSelected = employeeIds.every(id => selectedEmployees.includes(id));
    
    if (allSelected) {
      setSelectedEmployees(prev => prev.filter(id => !employeeIds.includes(id)));
    } else {
      setSelectedEmployees(prev => [...new Set([...prev, ...employeeIds])]);
    }
  };

  const handleAssignSelected = async () => {
    if (!rule || selectedEmployees.length === 0) return;

    // Filter to only unassigned employees from selection
    const employeesToAssign = selectedEmployees.filter(id => 
      !currentlyAssignedIds.includes(id)
    );

    if (employeesToAssign.length === 0) {
      toast({
        title: "No Changes Needed",
        description: "Selected employees are already assigned to this rule.",
        variant: "default",
      });
      return;
    }

    try {
      // Validate assignment first
      const validation = await validateAssignmentMutation.mutateAsync({
        ruleId: rule.id,
        profileIds: employeesToAssign
      });

      if (!validation.valid) {
        toast({
          title: "Assignment Validation Failed",
          description: validation.errors.join(', '),
          variant: "destructive",
        });
        return;
      }

      // Proceed with assignment
      await assignRuleMutation.mutateAsync({
        ruleId: rule.id,
        profileIds: employeesToAssign,
        skipValidation: true // Already validated
      });

      setSelectedEmployees([]);
    } catch (error) {
      console.error('Assignment error:', error);
    }
  };

  const handleUnassignSelected = async () => {
    if (!rule || selectedEmployees.length === 0) return;

    // Filter to only assigned employees from selection
    const employeesToUnassign = selectedEmployees.filter(id => 
      currentlyAssignedIds.includes(id)
    );

    if (employeesToUnassign.length === 0) {
      toast({
        title: "No Changes Needed",
        description: "Selected employees are not assigned to this rule.",
        variant: "default",
      });
      return;
    }

    try {
      // Unassign each employee
      await Promise.all(
        employeesToUnassign.map(profileId =>
          unassignRuleMutation.mutateAsync({ ruleId: rule.id, profileId })
        )
      );

      setSelectedEmployees([]);
    } catch (error) {
      console.error('Unassignment error:', error);
    }
  };

  const getRuleTypeColor = (ruleType: string) => {
    switch (ruleType) {
      case 'hourly_rate': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'salary': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'overtime': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
      case 'allowance': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
      case 'deduction': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'bonus': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  const formatRuleValue = (rule: CompensationRule) => {
    // currencyCode now comes from component scope (hook called at top level)
    switch (rule.calculation_method) {
      case 'fixed_amount': {
        const amount = rule.base_rate || 0;
        const formattedAmount = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: rule.rule_type === 'salary' ? 0 : 2,
          maximumFractionDigits: rule.rule_type === 'salary' ? 0 : 2
        }).format(amount);
        
        // Add appropriate suffix based on rule type
        if (rule.rule_type === 'salary') {
          const salaryMode = (rule as any).salary_mode || 'annual';
          return salaryMode === 'monthly_fixed' ? `${formattedAmount}/month` : `${formattedAmount}/year`;
        } else if (rule.rule_type === 'hourly_rate') {
          return `${formattedAmount}/hr`;
        } else {
          // allowance, bonus, deduction - no time-based suffix
          return formattedAmount;
        }
      }
      case 'percentage': 
        return `${rule.percentage_rate || 0}%`;
      case 'multiplier': 
        return `${rule.multiplier_rate || 1}x`;
      default: 
        return 'Variable';
    }
  };

  const isLoading = assignRuleMutation.isPending || unassignRuleMutation.isPending || validateAssignmentMutation.isPending;

  if (!rule) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Assign Rule to Employees
          </DialogTitle>
          <DialogDescription>
            Manage which employees are assigned to this compensation rule.
          </DialogDescription>
        </DialogHeader>

        {/* Rule Summary Card */}
        <Card className="mb-4">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <DollarSign className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{rule.rule_name}</CardTitle>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={getRuleTypeColor(rule.rule_type)}>
                      {rule.rule_type.replace('_', ' ')}
                    </Badge>
                    <span className="text-sm font-medium text-muted-foreground">
                      {formatRuleValue(rule)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-muted-foreground">Currently Assigned</div>
                <div className="text-2xl font-bold">{assignedEmployees.length}</div>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Search and Actions */}
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search employees by name, email, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleAssignSelected}
              disabled={isLoading || selectedEmployees.length === 0}
              size="sm"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <UserCheck className="mr-2 h-4 w-4" />
              Assign Selected
            </Button>
            <Button
              variant="outline"
              onClick={handleUnassignSelected}
              disabled={isLoading || selectedEmployees.length === 0}
              size="sm"
            >
              <UserX className="mr-2 h-4 w-4" />
              Unassign Selected
            </Button>
          </div>
        </div>

        {/* Employee Lists */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-2 gap-4 h-full">
            {/* Assigned Employees */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Assigned ({assignedEmployees.length})
                  </CardTitle>
                  {assignedEmployees.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSelectAll(assignedEmployees)}
                    >
                      {assignedEmployees.every(emp => selectedEmployees.includes(emp.id)) ? 'Deselect All' : 'Select All'}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {assignmentsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : assignedEmployees.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No employees assigned</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {assignedEmployees.map((employee: any) => (
                      <div
                        key={employee.id}
                        className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer"
                        onClick={() => handleEmployeeToggle(employee.id)}
                      >
                        <Checkbox
                          checked={selectedEmployees.includes(employee.id)}
                          onChange={() => handleEmployeeToggle(employee.id)}
                        />
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={employee.avatar_url} />
                          <AvatarFallback className="text-xs">
                            {getEmployeeInitials(employee)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {employee.first_name} {employee.last_name}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {employee.email}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {employee.role}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Unassigned Employees */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    Available ({unassignedEmployees.length})
                  </CardTitle>
                  {unassignedEmployees.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSelectAll(unassignedEmployees)}
                    >
                      {unassignedEmployees.every(emp => selectedEmployees.includes(emp.id)) ? 'Deselect All' : 'Select All'}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {employeesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : unassignedEmployees.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>All employees assigned</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {unassignedEmployees.map((employee: any) => (
                      <div
                        key={employee.id}
                        className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer"
                        onClick={() => handleEmployeeToggle(employee.id)}
                      >
                        <Checkbox
                          checked={selectedEmployees.includes(employee.id)}
                          onChange={() => handleEmployeeToggle(employee.id)}
                        />
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={employee.avatar_url} />
                          <AvatarFallback className="text-xs">
                            {getEmployeeInitials(employee)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {employee.first_name} {employee.last_name}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {employee.email}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {employee.role}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer */}
        <Separator />
        <div className="flex items-center justify-between pt-4">
          <div className="text-sm text-muted-foreground">
            {selectedEmployees.length > 0 && (
              <span>{selectedEmployees.length} employee(s) selected</span>
            )}
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}